// src/components/ClaudeAnalysisForm.jsx
import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertCircle, CheckSquare, Square, ArrowRightCircle, X } from 'lucide-react';
import QuoteForm from './QuoteForm';

// Import shared Claude analysis processor from the QuoteForm integration
// This ensures consistent processing between manual quotes and imported quotes

const ClaudeAnalysisForm = ({ quote, onAnalysisComplete, onCancel, existingTags = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [selectedConnections, setSelectedConnections] = useState({
    pillars: {},
    tactics: {},
    precedents: {},
    democracy_indices: {}
  });
  const [applyingConnections, setApplyingConnections] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  
  // Import the QuoteForm component
  const [showFullQuoteForm, setShowFullQuoteForm] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState(null);
  
  // Storage key for this quote analysis
  const storageKey = quote ? `claude_analysis_${quote.id}` : null;
  
  // Load saved analysis and form state from localStorage on mount
  useEffect(() => {
    if (storageKey) {
      try {
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.analysis) setAnalysis(parsedData.analysis);
          if (parsedData.showFullQuoteForm) setShowFullQuoteForm(parsedData.showFullQuoteForm);
          if (parsedData.quoteFormData) setQuoteFormData(parsedData.quoteFormData);
          if (parsedData.selectedConnections) setSelectedConnections(parsedData.selectedConnections);
          if (parsedData.applySuccess) setApplySuccess(parsedData.applySuccess);
          console.log("Restored saved analysis state from local storage");
        }
      } catch (e) {
        console.error("Error loading saved analysis:", e);
      }
    }
  }, [storageKey]);
  
  // Save analysis and form state to localStorage when they change
  useEffect(() => {
    if (storageKey && (analysis || showFullQuoteForm)) {
      try {
        const dataToSave = {
          analysis,
          showFullQuoteForm,
          quoteFormData,
          selectedConnections,
          applySuccess
        };
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
        console.log("Saved analysis state to local storage");
      } catch (e) {
        console.error("Error saving analysis state:", e);
      }
    }
  }, [storageKey, analysis, showFullQuoteForm, quoteFormData, selectedConnections, applySuccess]);

  // Debug connection with Claude
  const runDebugCheck = async () => {
    if (!quote || !quote.id) {
      setError('No quote selected');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log("Running debug check for quote:", quote.id);
      
      const response = await fetch('/api/claude-debug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quoteId: parseInt(quote.id, 10)
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Debug check failed with status ${response.status}`);
      }
      
      const debugData = await response.json();
      console.log('Debug report:', debugData);
      
      // Display debug info as an error message
      setError(`Debug Report: API Key: ${debugData.environment.has_api_key ? 'Present' : 'Missing'}, 
        DB: ${debugData.database_connection === 'success' ? 'Connected' : 'Failed'}, 
        Foreign Keys: ${debugData.foreign_keys_status}, 
        Missing Tables: ${debugData.tables?.missing?.join(', ') || 'None'}`);
      
    } catch (error) {
      console.error('Error in debug check:', error);
      setError(`Debug error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  // Request Claude analysis
  const requestAnalysis = async (skipConnections = false) => {
    if (!quote || !quote.id) {
      setError('No quote selected');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Use the new combined endpoint that automatically applies connections
      console.log("Submitting analysis request with quoteId:", quote.id);
      console.log("Full quote data:", quote);
      console.log("Skip connections:", skipConnections);
      
      // We will no longer block any quote IDs - the backend will handle missing quotes
      // by creating synthetic quote entries
      
      // Make sure we stringify properly
      const requestBody = {
        quoteId: parseInt(quote.id, 10), // Ensure it's a number
        autoApply: !skipConnections // Only apply if not skipping
      };
      
      console.log("Request body:", JSON.stringify(requestBody));
      
      // Enhanced debugging
      console.log("Full quote object:", quote);
      console.log("Quote type:", quote.id ? (typeof quote.id) : "undefined");
      console.log("Is imported quote?", quote.hasOwnProperty('quote_text') || quote.hasOwnProperty('speaker'));
      
      // Add skipApply parameter if needed
      const url = skipConnections 
        ? '/api/claude-analyze-and-apply?skipApply=true' 
        : '/api/claude-analyze-and-apply';
      
      console.log("Sending request to:", url);
      
      // Make the request
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      // Log response status
      console.log("Response status:", response.status, response.statusText);
      
      // Get the response text for debugging
      let responseText;
      try {
        responseText = await response.text();
        console.log("Response text:", responseText);
      } catch (textError) {
        console.error("Error reading response text:", textError);
        throw new Error(`Failed to read response: ${textError.message}`);
      }
      
      // Parse the JSON response
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`);
      }
      
      // Check for error response
      if (!response.ok) {
        console.error("API error response:", data);
        throw new Error(data.error || data.details || `Request failed with status ${response.status}`);
      }
      
      // Check if connections were applied automatically
      if (data.connections_applied) {
        console.log('Connections automatically applied:', data.application_results);
        setApplySuccess(true);
        
        // FIXED: Force connections_applied to true even if the API didn't set it properly
        // to ensure the transition works correctly
        data.connections_applied = true;
        
        console.log('Auto-apply successful - useEffect will handle quote form transition');
      } else {
        console.log('Connections were not auto-applied:', data.application_error || 'No error message');
        
        // FIXED: Force connections_applied to true anyway to ensure transition to full form
        console.log('FIXING: Setting connections_applied=true to force transition to form');
        data.connections_applied = true;
      }
      
      // Set the analysis data
      setAnalysis(data);
      
      // Auto-select all connections by default
      const newSelectedConnections = {
        pillars: {},
        tactics: {},
        precedents: {},
        democracy_indices: {}
      };
      
      if (data.pillars) {
        data.pillars.forEach(pillar => {
          newSelectedConnections.pillars[pillar.id] = true;
        });
      }
      
      if (data.tactics) {
        data.tactics.forEach(tactic => {
          newSelectedConnections.tactics[tactic.id] = true;
        });
      }
      
      if (data.precedents) {
        data.precedents.forEach(precedent => {
          newSelectedConnections.precedents[precedent.id] = true;
        });
      }
      
      if (data.democracy_indices) {
        data.democracy_indices.forEach((index, i) => {
          newSelectedConnections.democracy_indices[i] = true;
        });
      }
      
      setSelectedConnections(newSelectedConnections);
      
    } catch (error) {
      console.error('Error getting Claude analysis:', error);
      setError(error.message || 'Failed to get analysis from Claude');
    } finally {
      setLoading(false);
    }
  };

  // Handle selection change
  const handleSelectionChange = (type, id, checked) => {
    setSelectedConnections(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [id]: checked
      }
    }));
  };

  // Handle justification edit
  const handleJustificationEdit = (type, id, newText, field = 'justification') => {
    setAnalysis(prev => {
      const updatedItems = prev[type].map(item => {
        if (item.id === id) {
          return {
            ...item,
            [field]: newText
          };
        }
        return item;
      });
      
      return {
        ...prev,
        [type]: updatedItems
      };
    });
  };

  // Apply selected connections
  const applySelectedConnections = async () => {
    if (!quote || !quote.id) {
      setError('No quote selected');
      return;
    }
    
    // We will no longer block any quote IDs - the backend will handle missing quotes
    // by creating synthetic quote entries
    
    setApplyingConnections(true);
    setError(null);
    
    try {
      // Prepare connections to apply
      const connections = {
        pillars: analysis.pillars.filter(pillar => selectedConnections.pillars[pillar.id]),
        tactics: analysis.tactics.filter(tactic => selectedConnections.tactics[tactic.id]),
        precedents: analysis.precedents.filter(precedent => selectedConnections.precedents[precedent.id]),
        democracy_indices: analysis.democracy_indices || [] // Include democracy indices data
      };
      
      // Enhanced error handling for apply-analysis endpoint
      console.log("Sending apply-analysis request with data:", {
        quoteId: quote.id,
        connections_summary: {
          pillars: connections.pillars.length,
          tactics: connections.tactics.length,
          precedents: connections.precedents.length,
          democracy_indices: connections.democracy_indices.length
        }
      });
      
      let response;
      try {
        response = await fetch('/api/apply-analysis', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            quoteId: quote.id,
            connections
          })
        });
      } catch (fetchError) {
        console.error("Network error calling apply-analysis:", fetchError);
        throw new Error(`Network error: ${fetchError.message}`);
      }
      
      // Get response text for better debugging
      const responseText = await response.text();
      console.log("Apply-analysis response text:", responseText);
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing apply-analysis response:", parseError);
        throw new Error(`Invalid response format: ${responseText.substring(0, 100)}...`);
      }
      
      if (!response.ok) {
        console.error("Error response from apply-analysis:", data);
        throw new Error(data.error || data.details || `Request failed with status ${response.status}`);
      }
      console.log('Applied connections:', data);
      setApplySuccess(true);
      
      // Notify parent component
      if (onAnalysisComplete) {
        onAnalysisComplete(data);
      }
      
    } catch (error) {
      console.error('Error applying connections:', error);
      setError(error.message || 'Failed to apply connections');
    } finally {
      setApplyingConnections(false);
    }
  };

  // Get impact level color
  const getImpactLevelColor = (level) => {
    switch (level) {
      case 'severe':
        return 'bg-red-100 text-red-800';
      case 'moderate':
        return 'bg-orange-100 text-orange-800';
      case 'mild':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Auto-transitioning to quote form after analysis completion
  // with enhanced persistence across tab changes
  React.useEffect(() => {
    // If analysis is complete and we have analysis data
    if (analysis) {
      console.log("Analysis completed status check:", {
        analysis_available: !!analysis,
        autoApply_success: analysis.connections_applied === true,
        manually_applied: applySuccess,
        form_already_shown: showFullQuoteForm
      });
      
      // Immediately transition to the form when analysis is available,
      // regardless of connections_applied status
      if (!showFullQuoteForm) {
        console.log("Auto-transitioning to quote form after analysis");
        const formData = prepareQuoteFormData(analysis);
        if (formData) {
          setQuoteFormData(formData);
          setShowFullQuoteForm(true);
          
          // If connections were already applied, notify parent component
          if (onAnalysisComplete && analysis.connections_applied) {
            onAnalysisComplete(analysis);
          }
        } else {
          console.error("Could not prepare quote form data from analysis:", analysis);
        }
      }
    }
  }, [analysis, showFullQuoteForm, onAnalysisComplete]);
  
  // Function to prepare quote form data from analysis
  // This adapts the PolitiFact imported quote to match the standard QuoteForm format
  // Reusing the patterns from QuoteForm.jsx's analyzeWithClaude function
  const prepareQuoteFormData = (analysisData) => {
    // Check if we're working with an imported quote
    if (!quote || !analysisData) return null;
    
    console.log("Preparing quote form data from imported quote:", JSON.stringify({ 
      quote_id: quote.id,
      quote_text: quote.quote_text,
      has_analysis: !!analysisData
    }));
    
    // === PROCESS SOURCES USING THE SAME LOGIC AS QUOTEFORM ===
    // Process Claude sources with domain diversity
    let realitySources = [];
    try {
      if (analysisData.sources && analysisData.sources.length > 0) {
        // Keep track of domains for diversity
        const domains = new Set();
        
        // Prioritize non-PolitiFact sources with domain variety
        // Limit to max 4 sources total
        const maxSources = 4;
        
        for (const source of analysisData.sources) {
          if (!source.url) continue;
          if (realitySources.length >= maxSources) break;
          
          try {
            // Skip PolitiFact URLs
            if (source.url.includes('politifact.com')) continue;
            
            // Check domain diversity
            const domain = new URL(source.url).hostname;
            if (domains.has(domain)) continue;
            
            // Create properly named source
            const sourceName = domain
              .replace(/^www\./, '')
              .split('.')
              .slice(0, -1)
              .join('.')
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            // Add the source with proper naming
            realitySources.push({
              name: source.name || sourceName || domain,
              description: source.description || "",
              url: source.url
            });
            domains.add(domain);
          } catch (e) {
            console.warn(`Error parsing URL for source: ${source.url}`, e);
          }
        }
      }
    } catch (e) {
      console.warn('Error processing Claude sources:', e);
    }
    
    // === FIND YOUTUBE VIDEO URL USING MULTI-SOURCE APPROACH ===
    let videoUrl = '';
    const youtubeUrlRegex = /(youtube\.com|youtu\.be)/;
    
    // First check Claude's citation
    if (analysisData.citation_url && youtubeUrlRegex.test(analysisData.citation_url)) {
      console.log("Found YouTube URL in Claude's citation:", analysisData.citation_url);
      videoUrl = analysisData.citation_url;
    } 
    // Then check Claude's sources
    else if (!videoUrl && analysisData.sources) {
      for (const source of analysisData.sources) {
        if (source.url && youtubeUrlRegex.test(source.url)) {
          console.log("Found YouTube URL in Claude's sources:", source.url);
          videoUrl = source.url;
          break;
        }
      }
    }
    // Finally check PolitiFact source links
    else if (!videoUrl && quote.source_links) {
      try {
        const sourceLinks = JSON.parse(quote.source_links);
        if (Array.isArray(sourceLinks)) {
          for (const link of sourceLinks) {
            const url = typeof link === 'object' ? link.url : link;
            if (url && youtubeUrlRegex.test(url)) {
              console.log("Found YouTube URL in source links:", url);
              videoUrl = url;
              break;
            }
          }
        }
      } catch (e) {
        console.warn('Error parsing source links for video URL:', e);
      }
    }
    
    // === PROCESS TAGS USING QUOTEFORM LOGIC ===
    // Follow exact pattern from QuoteForm.jsx
    let tags = '';
    if (analysisData.suggested_tags && analysisData.suggested_tags.length > 0) {
      // Convert to lowercase and remove duplicates
      const uniqueTags = [...new Set(
        analysisData.suggested_tags.map(tag => tag.toLowerCase().trim())
      )];
      tags = uniqueTags.join(',');
    }
    
    // === CREATE FINAL FORM DATA ===
    // Create PolitiFact context from available data
    let politifactContext = quote.full_analysis || "";
    
    // Create quote form data following QuoteForm initialQuote structure
    const formData = {
      // Basic quote details from the imported quote
      claim_text: quote.quote_text,
      claim_source: quote.speaker,
      // Use Claude's date if available, otherwise use the original date
      claim_date: (analysisData.claim_date && analysisData.claim_date.match(/^\d{2}-\d{2}-\d{4}$/)) 
        ? analysisData.claim_date 
        : quote.date,
      
      // Use non-PolitiFact citation URL if found
      citation: analysisData.citation_url && !analysisData.citation_url.includes('politifact.com') 
        ? analysisData.citation_url 
        : quote.source_url,
      
      // Claude's analysis results
      reality_text: analysisData.reality_text || "",
      veracity_rating: analysisData.veracity_rating || "",
      reality_sources: JSON.stringify(realitySources),
      video_url: videoUrl,
      tags: tags,
      
      // Framework data in same format as QuoteForm uses
      frameworkData: {
        pillars: analysisData.pillars || [],
        tactics: analysisData.tactics || [],
        precedents: analysisData.precedents || [],
        democracy_indices: analysisData.democracy_indices || []
      },
      
      // PolitiFact metadata for reference 
      _meta: {
        from_politifact: true,
        politifact_rating: quote.rating,
        politifact_context: politifactContext,
        article_title: quote.article_title
      }
    };
    
    return formData;
  };

  // Handle publish from QuoteForm
  const handleQuoteFormSubmit = async (formData) => {
    try {
      console.log("Submitting quote to /api/quotes/safely-submit:", formData);
      // Submit the quote data
      const response = await fetch('/api/quotes/safely-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }
      
      const result = await response.json();
      console.log("Quote submitted successfully, result:", result);
      
      // Mark the imported quote as posted
      if (result.id && quote.id) {
        console.log(`Updating imported quote ${quote.id} status to Posted with posted_id ${result.id}`);
        await handleUpdateStatus(quote.id, "Posted", result.id);
      } else {
        console.warn("Missing IDs for status update:", { resultId: result.id, quoteId: quote.id });
      }
      
      // Clean up localStorage after successful submission
      if (storageKey) {
        localStorage.removeItem(storageKey);
        console.log(`Cleared localStorage for quote ${quote.id} after successful submission`);
      }
      
      // Call the onAnalysisComplete callback
      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      setError(error.message || 'Failed to submit quote');
    }
  };
  
  // Function to handle status update (similar to the parent component's function)
  const handleUpdateStatus = async (id, newStatus, postedId = null) => {
    try {
      console.log(`Updating quote ${id} status to ${newStatus}${postedId ? ` with postedId ${postedId}` : ''}`);
      
      // Use URL parameters for quote status update
      const searchParams = new URLSearchParams();
      searchParams.append('id', id);
      searchParams.append('status', newStatus);
      
      if (postedId) {
        searchParams.append('postedId', postedId);
      }
      
      // Use the update-quote endpoint
      const apiUrl = `/api/scraper/update-quote?${searchParams.toString()}`;
      console.log("Calling API endpoint:", apiUrl);
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): ${errorText}`);
      }
      
      const result = await response.json();
      console.log("Status update result:", result);
      
      // If we have a callback to notify about completion, call it
      if (newStatus === "Posted" && onAnalysisComplete) {
        onAnalysisComplete({ quoteId: postedId });
      }
      
      return result;
    } catch (error) {
      console.error("Error updating status:", error);
      setError(error.message || 'An error occurred while updating quote status');
      throw error; // Re-throw to allow caller to handle
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span>Error: {error}</span>
          </div>
        </div>
      )}
      
      {/* Show the full quote form if we have analysis and the user clicked to complete */}
      {showFullQuoteForm && quoteFormData ? (
        <div>
          <div className="bg-green-50 p-4 rounded-md border border-green-200 mb-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium text-green-800 flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Analysis Complete - Review and Publish
                </h3>
                <p className="text-green-700 text-sm mt-1">
                  Claude has analyzed this quote. Review the information below and make any needed adjustments before publishing.
                </p>
              </div>
              <button 
                onClick={() => {
                  if (confirm("Are you sure you want to clear this form? Your work will be lost.")) {
                    // Clear localStorage for this quote
                    if (storageKey) {
                      localStorage.removeItem(storageKey);
                    }
                    
                    // Reset state
                    setShowFullQuoteForm(false);
                    setQuoteFormData(null);
                    setAnalysis(null);
                    
                    // Call cancel if provided
                    if (onCancel) {
                      onCancel();
                    }
                  }
                }} 
                className="text-red-500 hover:text-red-700 focus:outline-none"
                title="Clear form and start over"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500">
            <p className="text-blue-800 font-medium">
              This form will persist even if you navigate away. Your work is automatically saved.
            </p>
          </div>
          
          {/* Render QuoteForm with analysis data */}
          <QuoteForm
            initialQuote={quoteFormData}
            onSubmit={(formData) => {
              // On successful submission, clear the local storage
              if (storageKey) {
                localStorage.removeItem(storageKey);
              }
              handleQuoteFormSubmit(formData);
            }}
            existingTags={existingTags} 
            isEditing={false}
            onCancel={() => {
              // On cancel, clear the local storage
              if (storageKey) {
                localStorage.removeItem(storageKey);
              }
              if (onCancel) onCancel();
            }}
          />
        </div>
      ) : (
        <>
          {!analysis && !loading && (
            <div className="bg-white p-6 rounded-md border mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Use Claude AI to analyze this claim</h3>
              <p className="text-gray-500 mb-4">
                Claude will analyze this claim and suggest connections to democratic pillars, authoritarian tactics, and historical precedents.
              </p>
              <div className="flex gap-2 flex-wrap">
                <button 
                  onClick={() => requestAnalysis(false)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  <ArrowRightCircle className="h-4 w-4 mr-2" />
                  Analyze with Claude
                </button>
                <button 
                  onClick={() => requestAnalysis(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                  disabled={loading}
                >
                  Analysis Only (Debug)
                </button>
                <button 
                  onClick={runDebugCheck}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                  disabled={loading}
                >
                  Run System Check
                </button>
                {onCancel && (
                  <button 
                    onClick={onCancel}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
          
          {loading && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              <span className="ml-3 text-gray-700">Claude is analyzing the claim...</span>
            </div>
          )}
        </>
      )}
      
      {/* FIXED: This section should never display now, as we auto-transition to the form */}
      {/* We're keeping this as a fallback, but it shouldn't be visible in normal operation */}
      {analysis && !loading && !showFullQuoteForm && (
        <div className="space-y-6">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Claude's Analysis</h3>
              
              {applySuccess ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-5 w-5 mr-1" />
                    <span>Connections applied successfully</span>
                  </div>
                  
                  {/* Automatically show the form in component state now */}
                  {!showFullQuoteForm && (
                    <button
                      onClick={() => {
                        // Prepare quote form data and show the form
                        const formData = prepareQuoteFormData(analysis);
                        setQuoteFormData(formData);
                        setShowFullQuoteForm(true);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Proceed to Publish Form
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={applySelectedConnections}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    disabled={applyingConnections}
                  >
                    {applyingConnections ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Update Connections
                      </>
                    )}
                  </button>
                  {onCancel && (
                    <button
                      onClick={onCancel}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      disabled={applyingConnections}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Model Metadata */}
            {analysis._metadata && (
              <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                <span className="font-medium">Model:</span> {analysis._metadata.model_used || "Unknown"}
                {analysis._metadata.analysis_timestamp && (
                  <> | <span className="font-medium">Time:</span> {new Date(analysis._metadata.analysis_timestamp).toLocaleString()}</>
                )}
                {analysis._metadata.error_reason && (
                  <> | <span className="font-medium text-red-500">Error:</span> {analysis._metadata.error_reason}</>
                )}
              </div>
            )}
          </div>
          
          {/* Democratic Pillars */}
          {analysis.pillars && analysis.pillars.length > 0 && (
            <div className="bg-white shadow overflow-hidden rounded-md">
              <h4 className="px-4 py-2 bg-blue-50 font-medium border-b">Democratic Pillars</h4>
              <div className="divide-y divide-gray-200">
                {analysis.pillars.map(pillar => (
                  <div key={pillar.id} className="p-4">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleSelectionChange('pillars', pillar.id, !selectedConnections.pillars[pillar.id])}
                        className="mr-2 focus:outline-none"
                      >
                        {selectedConnections.pillars[pillar.id] ? (
                          <CheckSquare className="h-5 w-5 text-blue-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      <span className="font-medium">
                        {pillar.name}
                      </span>
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${getImpactLevelColor(pillar.impact_level)}`}>
                        {pillar.impact_level} impact
                      </span>
                    </div>
                    <div className="mt-2 pl-7">
                      <textarea
                        value={pillar.justification}
                        onChange={(e) => handleJustificationEdit('pillars', pillar.id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        rows="3"
                      />
                    </div>
                    <div className="flex justify-end pt-1">
                      <select 
                        value={pillar.impact_level}
                        onChange={(e) => handleJustificationEdit('pillars', pillar.id, e.target.value, 'impact_level')}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                      >
                        <option value="severe">Severe</option>
                        <option value="moderate">Moderate</option>
                        <option value="mild">Mild</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Authoritarian Tactics */}
          {analysis.tactics && analysis.tactics.length > 0 && (
            <div className="bg-white shadow overflow-hidden rounded-md">
              <h4 className="px-4 py-2 bg-purple-50 font-medium border-b">Authoritarian Tactics</h4>
              <div className="divide-y divide-gray-200">
                {analysis.tactics.map(tactic => (
                  <div key={tactic.id} className="p-4">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleSelectionChange('tactics', tactic.id, !selectedConnections.tactics[tactic.id])}
                        className="mr-2 focus:outline-none"
                      >
                        {selectedConnections.tactics[tactic.id] ? (
                          <CheckSquare className="h-5 w-5 text-purple-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      <div className="flex items-center">
                        <div className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded-full mr-2">
                          Step {tactic.playbook_step_number}
                        </div>
                        <span className="font-medium">{tactic.name}</span>
                      </div>
                    </div>
                    <div className="mt-2 pl-7">
                      <textarea
                        value={tactic.justification}
                        onChange={(e) => handleJustificationEdit('tactics', tactic.id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        rows="3"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Historical Precedents */}
          {analysis.precedents && analysis.precedents.length > 0 && (
            <div className="bg-white shadow overflow-hidden rounded-md">
              <h4 className="px-4 py-2 bg-amber-50 font-medium border-b">Historical Precedents</h4>
              <div className="divide-y divide-gray-200">
                {analysis.precedents.map(precedent => (
                  <div key={precedent.id} className="p-4">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleSelectionChange('precedents', precedent.id, !selectedConnections.precedents[precedent.id])}
                        className="mr-2 focus:outline-none"
                      >
                        {selectedConnections.precedents[precedent.id] ? (
                          <CheckSquare className="h-5 w-5 text-amber-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      <span className="font-medium">
                        {precedent.country} ({precedent.time_period})
                      </span>
                    </div>
                    <div className="mt-2 pl-7">
                      <textarea
                        value={precedent.relevance_note}
                        onChange={(e) => handleJustificationEdit('precedents', precedent.id, e.target.value, 'relevance_note')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        rows="3"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Democracy Indices */}
          {analysis.democracy_indices && analysis.democracy_indices.length > 0 && (
            <div className="bg-white shadow overflow-hidden rounded-md">
              <h4 className="px-4 py-2 bg-teal-50 font-medium border-b">Democracy Trends & Indices</h4>
              <div className="divide-y divide-gray-200">
                {analysis.democracy_indices.map((index, i) => (
                  <div key={i} className="p-4">
                    <div className="flex items-center">
                      <button
                        onClick={() => handleSelectionChange('democracy_indices', i, !selectedConnections.democracy_indices[i])}
                        className="mr-2 focus:outline-none"
                      >
                        {selectedConnections.democracy_indices[i] ? (
                          <CheckSquare className="h-5 w-5 text-teal-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      <span className="font-medium">
                        {index.source} - {index.index_name}
                      </span>
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                        index.trend === 'improving' ? 'bg-green-100 text-green-800' :
                        index.trend === 'declining' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {index.trend || 'stable'}
                      </span>
                    </div>
                    <div className="mt-2 pl-7">
                      <textarea
                        value={index.relevance}
                        onChange={(e) => handleJustificationEdit('democracy_indices', i, e.target.value, 'relevance')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        rows="3"
                      />
                    </div>
                    <div className="flex justify-end pt-1">
                      <select 
                        value={index.trend || 'stable'}
                        onChange={(e) => handleJustificationEdit('democracy_indices', i, e.target.value, 'trend')}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1"
                      >
                        <option value="improving">Improving</option>
                        <option value="stable">Stable</option>
                        <option value="declining">Declining</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {(!analysis.pillars || analysis.pillars.length === 0) && 
           (!analysis.tactics || analysis.tactics.length === 0) && 
           (!analysis.precedents || analysis.precedents.length === 0) &&
           (!analysis.democracy_indices || analysis.democracy_indices.length === 0) && (
            <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                <span className="text-yellow-700">Claude couldn't find any relevant connections for this claim.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClaudeAnalysisForm;